type SegmentLike = { speaker_id: string };
type ChunkLike = { diarisedTranscript?: { segments: SegmentLike[] } | null };

/**
 * Stamps a blank `speaker_id` with the previous labelled segment's id as
 * diarised chunks are ingested.
 *
 * The transcription service leaves words in the gap between two diarised
 * spans unattributed. The previous speaker is the display default so the
 * fragment reads as the end of that turn and reassignment moves it with
 * the turn.
 *
 * An undiarised chunk (no `diarisedTranscript`, or empty `segments`)
 * resets inheritance so a later blank cannot join a speaker from before
 * the gap.
 *
 * This only stamps the derived live/backup payload. The transcription
 * service still stores the blank `speaker_id` until a later PATCH
 * writes the attribution.
 *
 * MOBILE-PARITY-DEBT: B1 — a blank speaker_id inherits the previous labelled
 * segment client-side, so turn / apply-to-all reassignment payloads can
 * include fragments the service stored as unattributed. iOS/Android would
 * need the same rule to behave identically. No mobile ticket yet: neither
 * app exposes speaker reassignment. Remove when transcription-service
 * attributes gap words itself.
 */
export const inheritBlankSpeakerIds = <C extends ChunkLike>(
  chunks: readonly C[]
): C[] => {
  let lastSpeakerId: string | null = null;

  return chunks.map((chunk) => {
    const diarisedTranscript = chunk.diarisedTranscript;
    const segments = diarisedTranscript?.segments;
    if (!segments?.length) {
      lastSpeakerId = null;
      return chunk;
    }

    let changed = false;
    const nextSegments = segments.map((segment) => {
      if (segment.speaker_id !== '') {
        lastSpeakerId = segment.speaker_id;
        return segment;
      }
      if (lastSpeakerId === null) {
        return segment;
      }
      changed = true;
      return { ...segment, speaker_id: lastSpeakerId };
    });

    if (!changed) {
      return chunk;
    }

    return {
      ...chunk,
      diarisedTranscript: {
        ...diarisedTranscript,
        segments: nextSegments,
      },
    };
  });
};
